import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { useAuth } from "../context/AuthContext";
import { authFetch, buildApiErrorFromResponse, getApiErrorMessage } from "../utils/apiClient";
import { getApiBaseUrl } from "../utils/apiBaseUrl";
import { buildInterviewConfig, readSavedInterviewConfig, saveInterviewConfig } from "../utils/accessMode";

const API_BASE = getApiBaseUrl();
const MAX_RESUME_BYTES = 5 * 1024 * 1024;
const EXPERIENCE_OPTIONS = [
  ["student", "Student"],
  ["early_career", "Early career"],
  ["mid", "Mid-career"],
  ["senior", "Senior/Lead"],
];
const INTERVIEW_TYPES = [
  ["technical", "Technical"],
  ["behavioral", "Behavioral"],
  ["mixed", "Mixed"],
];
const PROFILE_FIELDS = [
  "candidateType", "fullNameOverride", "stateCode", "city", "countryCode", "region", "timezone",
  "universityName", "universityNormalized", "degreeLevel", "degreeName", "branchSpecialization",
  "graduationYear", "currentYearOfStudy", "experienceLevel", "primaryStream", "targetRoles",
  "targetCompanies", "skillsSelfReported", "seniority", "yearsOfExperience", "currentTitle",
  "targetInterviewFormat", "targetJobDescription", "targetJobUrl", "resumeUrl", "linkedinUrl",
  "githubUrl", "portfolioUrl", "resumeText", "resumeDraft", "baselineCapture", "consentDataUse",
  "consentContact",
];

function profilePayload(profile, overrides) {
  const payload = {};
  PROFILE_FIELDS.forEach((key) => {
    if (profile?.[key] !== undefined) payload[key] = profile[key];
  });
  return {
    candidateType: "professional",
    targetRoles: [],
    skillsSelfReported: [],
    targetCompanies: [],
    consentContact: false,
    ...payload,
    ...overrides,
  };
}

function experienceLabel(value) {
  return EXPERIENCE_OPTIONS.find(([key]) => key === value)?.[1] || value || "Not set";
}

function savedExperienceLevel(profile) {
  const explicitLevel = String(profile?.experienceLevel || profile?.seniority || "").trim().toLowerCase();
  const aliases = {
    student: "student",
    intern: "student",
    early_career: "early_career",
    junior: "early_career",
    foundation: "early_career",
    mid: "mid",
    growth: "mid",
    advanced: "senior",
    senior: "senior",
    lead: "senior",
    leadership: "senior",
  };
  if (aliases[explicitLevel]) return aliases[explicitLevel];
  if (profile?.yearsOfExperience == null || profile.yearsOfExperience === "") {
    return explicitLevel ? "mid" : "";
  }
  const years = Number(profile?.yearsOfExperience);
  if (!Number.isFinite(years)) return explicitLevel ? "mid" : "";
  if (years === 0) return "student";
  if (years < 3) return "early_career";
  if (years < 7) return "mid";
  return "senior";
}

function yearsForLevel(value, existing, existingLevel) {
  if (existingLevel === value && existing != null && existing !== "") return Number(existing);
  if (value === "student") return 0;
  if (value === "early_career") return 1;
  if (value === "mid") return 4;
  if (value === "senior") return 8;
  return undefined;
}

function targetRolesForQuickSave(profile, role) {
  const normalizeRole = (value) => (typeof value === "string" ? value.trim().slice(0, 120) : "");
  const selectedRole = normalizeRole(role);
  const existingRoles = Array.isArray(profile?.targetRoles) ? profile.targetRoles : [];
  return [...new Set([selectedRole, ...existingRoles.map(normalizeRole)].filter(Boolean))].slice(0, 10);
}

function mergeProfileSkills(existingSkills, parsedSkills) {
  const existing = Array.isArray(existingSkills) ? existingSkills : [];
  const incoming = Array.isArray(parsedSkills) ? parsedSkills : [];
  const skillKey = (skill) => String(
    typeof skill === "string" ? skill : skill?.key || skill?.skill || skill?.label || "",
  ).trim().toLowerCase();
  const incomingByKey = new Map(incoming.map((skill) => [skillKey(skill), skill]).filter(([key]) => key));
  const merged = existing.map((skill) => {
    const key = skillKey(skill);
    const next = incomingByKey.get(key);
    if (!next) return skill;
    incomingByKey.delete(key);
    return skill && typeof skill === "object" && next && typeof next === "object"
      ? { ...skill, ...next }
      : next;
  });
  return [...merged, ...incomingByKey.values()];
}

function isLaunchReadyProfile(profile) {
  const roles = Array.isArray(profile?.targetRoles)
    ? profile.targetRoles.some((item) => typeof item === "string" && item.trim())
    : false;
  const hasTarget = roles || Boolean(String(profile?.targetJobDescription || "").trim());
  return Boolean(profile?.consentDataUse && hasTarget && savedExperienceLevel(profile));
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "");
    reader.onerror = () => reject(new Error("Could not read the selected resume."));
    reader.readAsDataURL(file);
  });
}

export default function PreInterviewForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();
  const recoveredConfig = useMemo(() => {
    const routeConfig = location.state?.interviewConfig;
    if (!routeConfig || typeof routeConfig !== "object") return null;
    const saved = readSavedInterviewConfig(sessionStorage);
    const savedConfig = saved.valid ? saved.config : null;
    return buildInterviewConfig({
      ...(savedConfig || {}),
      ...routeConfig,
      type: location.state?.type || routeConfig.type || savedConfig?.type,
    });
  }, [location.state?.interviewConfig, location.state?.type]);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [retryTick, setRetryTick] = useState(0);
  const [role, setRole] = useState(location.state?.role || recoveredConfig?.role || "");
  const [experienceLevel, setExperienceLevel] = useState(recoveredConfig?.jobLevel || "");
  const [selectedType, setSelectedType] = useState(location.state?.type || recoveredConfig?.type || "technical");
  const [dataConsent, setDataConsent] = useState(false);
  const [targetJobDescription, setTargetJobDescription] = useState(recoveredConfig?.targetJobDescription || "");
  const [targetJobUrl, setTargetJobUrl] = useState(recoveredConfig?.targetJobUrl || "");
  const [resumeDraft, setResumeDraft] = useState(null);
  const [resumeParsing, setResumeParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const recoveryMessage = location.state?.recoveryMessage || "";

  const profileExperienceLevel = savedExperienceLevel(profile);
  const returningUser = isLaunchReadyProfile(profile);
  const parsedFields = resumeDraft?.fields || {};
  const parsedSkills = parsedFields.skillsSelfReported?.value || [];
  const savedSkills = useMemo(
    () => (Array.isArray(profile?.skillsSelfReported) ? profile.skillsSelfReported : []),
    [profile?.skillsSelfReported],
  );

  useEffect(() => {
    let mounted = true;
    async function loadProfile() {
      setProfileLoading(true);
      setLoadError("");
      try {
        const token = await getToken();
        let response = await authFetch(`${API_BASE}/api/profile`, token, { method: "GET" });
        if (response?.status === 404) {
          const legacyResponse = await authFetch(`${API_BASE}/api/profile/me`, token, { method: "GET" });
          if (legacyResponse) response = legacyResponse;
        }
        if (response.status === 404) {
          if (mounted) setProfile(null);
          return;
        }
        if (!response.ok) {
          throw await buildApiErrorFromResponse(response, { defaultMessage: "Failed to load your profile." });
        }
        const body = await response.json();
        const loaded = body?.profile || body;
        if (!mounted) return;
        setProfile(loaded);
        setRole(location.state?.role || recoveredConfig?.role || loaded.targetRoles?.[0] || loaded.currentTitle || "");
        setExperienceLevel(recoveredConfig?.jobLevel || savedExperienceLevel(loaded));
        if (!location.state?.type) {
          const savedFormat = String(loaded.targetInterviewFormat || "").trim().toLowerCase();
          setSelectedType(INTERVIEW_TYPES.some(([value]) => value === savedFormat) ? savedFormat : recoveredConfig?.type || "technical");
        }
        setDataConsent(Boolean(loaded.consentDataUse));
        setTargetJobDescription(recoveredConfig?.targetJobDescription || loaded.targetJobDescription || "");
        setTargetJobUrl(recoveredConfig?.targetJobUrl || loaded.targetJobUrl || "");
      } catch (loadErr) {
        if (mounted) setLoadError(getApiErrorMessage(loadErr, { defaultMessage: "Failed to load your profile." }));
      } finally {
        if (mounted) setProfileLoading(false);
      }
    }
    loadProfile();
    return () => { mounted = false; };
  }, [getToken, location.state?.role, location.state?.type, recoveredConfig, retryTick]);

  const summarySkills = useMemo(
    () => savedSkills.map((skill) => (typeof skill === "string" ? skill : skill?.label || skill?.key)).filter(Boolean).slice(0, 5),
    [savedSkills],
  );

  const handleResume = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError("");
    const lowerName = file.name.toLowerCase();
    if (!lowerName.endsWith(".pdf") && !lowerName.endsWith(".docx")) {
      setError("Upload a PDF or DOCX resume.");
      return;
    }
    if (file.size > MAX_RESUME_BYTES) {
      setError("Resume must be 5 MB or smaller.");
      return;
    }
    setResumeParsing(true);
    try {
      const token = await getToken();
      const contentBase64 = await readFileAsBase64(file);
      const response = await authFetch(`${API_BASE}/api/profile/resume-draft`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fileName: file.name, contentBase64 }),
      });
      if (!response.ok) {
        throw await buildApiErrorFromResponse(response, { defaultMessage: "Could not parse the resume." });
      }
      const body = await response.json();
      setResumeDraft(body.draft);
      const fields = body.draft?.fields || {};
      setRole((current) => current || fields.currentTitle?.value || "");
      const years = fields.yearsOfExperience?.value;
      if (years != null) setExperienceLevel(years === 0 ? "student" : years >= 7 ? "senior" : years >= 3 ? "mid" : "early_career");
    } catch (parseErr) {
      setError(getApiErrorMessage(parseErr, { defaultMessage: "Could not parse the resume. Enter role and experience manually." }));
    } finally {
      setResumeParsing(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (!role.trim() && !targetJobDescription.trim()) {
      setError("Enter a target role or job description.");
      return;
    }
    if (!experienceLevel) {
      setError("Choose an experience level.");
      return;
    }
    if (!dataConsent) {
      setError("Profile data-use consent is required.");
      return;
    }

    setSaving(true);
    try {
      const token = await getToken();
      const parsedName = parsedFields.fullNameOverride?.value;
      const parsedYears = parsedFields.yearsOfExperience?.value;
      const parsedRole = parsedFields.currentTitle?.value;
      const sourceYears = parsedYears ?? profile?.yearsOfExperience;
      const sourceLevel = parsedYears != null
        ? savedExperienceLevel({ yearsOfExperience: parsedYears })
        : profileExperienceLevel;
      const payload = profilePayload(profile, {
        candidateType: experienceLevel === "student" ? "student" : "professional",
        fullNameOverride: parsedName || profile?.fullNameOverride || null,
        targetRoles: targetRolesForQuickSave(profile, role),
        seniority: experienceLevel,
        experienceLevel,
        yearsOfExperience: yearsForLevel(experienceLevel, sourceYears, sourceLevel),
        currentTitle: profile?.currentTitle || parsedRole || null,
        targetInterviewFormat: recoveredConfig?.targetInterviewFormat || profile?.targetInterviewFormat || selectedType,
        targetJobDescription: targetJobDescription.trim() || null,
        targetJobUrl: targetJobUrl.trim() || null,
        skillsSelfReported: mergeProfileSkills(savedSkills, parsedSkills),
        primaryStream: profile?.primaryStream || parsedSkills[0]?.key || null,
        resumeText: resumeDraft?.rawText || profile?.resumeText || null,
        resumeDraft: resumeDraft || profile?.resumeDraft || null,
        consentDataUse: true,
      });
      const saveResponse = await authFetch(`${API_BASE}/api/profile`, token, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!saveResponse.ok) {
        throw await buildApiErrorFromResponse(saveResponse, { defaultMessage: "Could not save your profile." });
      }

      const sessionId = `session_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      saveInterviewConfig(buildInterviewConfig({
        ...(recoveredConfig || {}),
        sessionId,
        type: selectedType,
        duration: recoveredConfig?.duration || 10,
        difficulty: recoveredConfig?.difficulty || "auto",
        role: role.trim() || undefined,
        jobLevel: experienceLevel,
        yearsExperience: payload.yearsOfExperience,
        targetInterviewFormat: recoveredConfig?.targetInterviewFormat || selectedType,
        targetJobDescription: targetJobDescription.trim() || undefined,
        targetJobUrl: targetJobUrl.trim() || undefined,
        questionMix: recoveredConfig?.questionMix || "balanced",
        interviewStyle: recoveredConfig?.interviewStyle || "neutral",
        selectedSkills: recoveredConfig?.selectedSkills || [],
      }), sessionStorage);
      navigate(`/interview/session/${sessionId}`);
    } catch (submitErr) {
      setError(getApiErrorMessage(submitErr, { defaultMessage: "Could not start the interview. Your entries are still here; try again." }));
    } finally {
      setSaving(false);
    }
  };

  if (profileLoading) {
    return <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center" }}><CircularProgress aria-label="Loading profile" /></Box>;
  }

  if (loadError) {
    return (
      <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center", p: 2 }}>
        <Stack spacing={2} sx={{ width: "100%", maxWidth: 520 }}>
          <Alert severity="error">{loadError}</Alert>
          <Button variant="contained" onClick={() => setRetryTick((value) => value + 1)}>Retry profile</Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100%", bgcolor: "background.default", px: { xs: 1.5, sm: 3 }, py: { xs: 2, md: 5 } }}>
      <Card sx={{ maxWidth: 760, mx: "auto", border: "1px solid", borderColor: "divider", boxShadow: "none" }}>
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Typography component="h1" variant="h4" sx={{ fontWeight: 800 }}>Interview quick start</Typography>
            <Typography color="text.secondary">Use your saved profile, choose the interview type, and start.</Typography>
          </Stack>
          {recoveryMessage && <Alert severity="warning" sx={{ mb: 2 }}>{recoveryMessage}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={3}>
              {returningUser ? (
                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 750 }}>Saved profile</Typography>
                      <Typography variant="h6">{role}</Typography>
                      <Typography color="text.secondary">
                        {experienceLabel(experienceLevel)}{profile?.yearsOfExperience != null ? ` · ${profile.yearsOfExperience} years` : ""}
                      </Typography>
                      {summarySkills.length > 0 && (
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                          {summarySkills.map((skill) => <Chip key={skill} label={skill} size="small" />)}
                        </Stack>
                      )}
                    </Box>
                    <Button variant="outlined" onClick={() => navigate("/onboarding")} sx={{ alignSelf: { xs: "stretch", sm: "flex-start" } }}>Edit profile</Button>
                  </Stack>
                </Box>
              ) : (
                <Stack spacing={2}>
                  <Button component="label" variant="outlined" disabled={resumeParsing} sx={{ alignSelf: { xs: "stretch", sm: "flex-start" } }}>
                    {resumeParsing ? "Parsing resume…" : "Upload resume (optional)"}
                    <input hidden type="file" aria-label="Resume upload" accept=".pdf,.docx" onChange={handleResume} />
                  </Button>
                  <TextField required fullWidth label="Target role" value={role} onChange={(event) => setRole(event.target.value)} placeholder="e.g. Backend Engineer" />
                  <FormControl fullWidth required>
                    <InputLabel id="experience-level-label">Experience level</InputLabel>
                    <Select labelId="experience-level-label" label="Experience level" value={experienceLevel} onChange={(event) => setExperienceLevel(event.target.value)}>
                      {EXPERIENCE_OPTIONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Stack>
              )}

              {resumeDraft && (
                <Alert severity="success" icon={false}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 750 }}>Resume ready to save</Typography>
                  <Typography variant="body2">
                    {[parsedFields.fullNameOverride?.value, parsedFields.currentTitle?.value,
                      parsedFields.yearsOfExperience?.value != null ? `${parsedFields.yearsOfExperience.value} years` : null]
                      .filter(Boolean).join(" · ") || "Review the role and experience below."}
                  </Typography>
                  {parsedSkills.length > 0 && <Typography variant="body2">Skills: {parsedSkills.map((skill) => skill.label || skill.key).join(", ")}</Typography>}
                </Alert>
              )}

              <FormControl component="fieldset">
                <FormLabel component="legend">Interview type</FormLabel>
                <RadioGroup row value={selectedType} onChange={(event) => setSelectedType(event.target.value)} aria-label="Interview type">
                  {INTERVIEW_TYPES.map(([value, label]) => <FormControlLabel key={value} value={value} control={<Radio />} label={label} />)}
                </RadioGroup>
              </FormControl>

              <Box component="details" sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2 }}>
                <Typography component="summary" sx={{ cursor: "pointer", fontWeight: 650 }}>Optional job description or URL</Typography>
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <TextField fullWidth label="Job URL" value={targetJobUrl} onChange={(event) => setTargetJobUrl(event.target.value)} />
                  <TextField fullWidth multiline minRows={3} label="Job description" value={targetJobDescription} onChange={(event) => setTargetJobDescription(event.target.value)} />
                </Stack>
              </Box>

              {!returningUser && (
                <FormControlLabel control={<Checkbox checked={dataConsent} onChange={(event) => setDataConsent(event.target.checked)} />} label="I consent to use my profile information to personalize interviews and feedback." />
              )}
              <Typography variant="body2" color="text.secondary">
                Your spoken answers are used to generate the interview report. Raw audio is not retained.
              </Typography>

              <Button type="submit" variant="contained" size="large" disabled={saving || resumeParsing} fullWidth>
                {saving ? "Saving and starting…" : "Start interview"}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
